import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Tab,
  Tabs,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from '@mui/material';
import {
  Save,
  Add,
  Edit,
  Delete,
  Business,
  Person,
  Security,
  Notifications,
  PhotoCamera,
  VpnKey,
  LocalShipping,
  Search,
  Category,
  Build,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  mobile_phone?: string;
  role: string;
  is_active: boolean;
  profile_picture?: string;
}

interface Supplier {
  id?: number;
  company: string;
  contact_name: string;
  primary_product: string;
  email: string;
  phone: string;
  address: string;
  address_2: string;
  address_3: string;
  city: string;
  county: string;
  postcode: string;
  notes: string;
  is_active: boolean;
}

interface CategoryItem {
  id?: number;
  name: string;
  sort_order: number;
  is_active: boolean;
}

interface Fitter {
  id?: number;
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  postcode: string;
  specialty: string;
  notes: string;
  is_active: boolean;
}

interface AddressOption {
  addressline1: string;
  addressline2: string;
  summaryline: string;
  organisation: string;
  buildingname: string;
  premise: string;
  street: string;
  dependentlocality: string;
  posttown: string;
  county: string;
  postcode: string;
  number: string;
  pobox: string;
  departmentname: string;
  subbuildingname: string;
  doubledependentlocality: string;
  thoroughfare: string;
  dependentthoroughfare: string;
  address1: string;
  postTown: string;
}

// No mock data - all users fetched from API

export default function Settings() {
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedProfileImage, setSelectedProfileImage] = useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null);
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUsers(userData);
      } else {
        showError('Failed to load users');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      showError('Error loading users');
    } finally {
      setLoading(false);
    }
  };

  // Company Settings State
  const [companySettings, setCompanySettings] = useState({
    company_name: "Gordon's Makeovers",
    address: '215 Church Street, Westhoughton, Bolton, BL5 3SW',
    phone: '01942 814 800',
    email: 'info@gordonsmakeovers.co.uk',
    website: 'gordonsmakeovers.co.uk',
    vat_number: 'GB123456789',
    default_vat_rate: '20.00',
  });

  // Notification Settings State
  const [notificationSettings, setNotificationSettings] = useState({
    email_notifications: true,
    lead_notifications: true,
    project_notifications: true,
    invoice_notifications: true,
    overdue_reminders: true,
  });

  // User Form State
  const [userForm, setUserForm] = useState({
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    mobile_phone: '',
    password: '',
    role: 'user',
    is_active: true,
  });

  // Password Change State
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [changingPasswordForUser, setChangingPasswordForUser] = useState<User | null>(null);

  // Supplier Management State
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [supplierForm, setSupplierForm] = useState<Supplier>({
    company: '',
    contact_name: '',
    primary_product: '',
    email: '',
    phone: '',
    address: '',
    address_2: '',
    address_3: '',
    city: '',
    county: '',
    postcode: '',
    notes: '',
    is_active: true,
  });
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  // Supplier CC Emails State
  const [supplierCcEmails, setSupplierCcEmails] = useState<string[]>([]);
  const [newCcEmail, setNewCcEmail] = useState('');
  const [loadingCcEmails, setLoadingCcEmails] = useState(false);

  // Fitter CC Emails State
  const [fitterCcEmails, setFitterCcEmails] = useState<string[]>([]);
  const [newFitterCcEmail, setNewFitterCcEmail] = useState('');
  const [loadingFitterCcEmails, setLoadingFitterCcEmails] = useState(false);

  // Fitter Management State
  const [fitters, setFitters] = useState<Fitter[]>([]);
  const [fitterDialogOpen, setFitterDialogOpen] = useState(false);
  const [editingFitter, setEditingFitter] = useState<Fitter | null>(null);
  const [fitterForm, setFitterForm] = useState<Fitter>({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postcode: '',
    specialty: '',
    notes: '',
    is_active: true,
  });
  const [loadingFitters, setLoadingFitters] = useState(false);

  // PAF Postcode Lookup State
  const [postcodeSearch, setPostcodeSearch] = useState('');
  const [addressOptions, setAddressOptions] = useState<AddressOption[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [postcodeDialogOpen, setPostcodeDialogOpen] = useState(false);

  // Category Management State
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryItem | null>(null);
  const [categoryForm, setCategoryForm] = useState<CategoryItem>({
    name: '',
    sort_order: 0,
    is_active: true,
  });
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Load suppliers, CC emails, fitters, and categories when tab is selected
  useEffect(() => {
    if (tabValue === 4) {
      loadSuppliers();
      loadSupplierCcEmails();
    }
    if (tabValue === 5) {
      loadFitters();
      loadFitterCcEmails();
    }
    if (tabValue === 6) {
      loadCategories();
    }
  }, [tabValue]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Load Supplier CC Emails
  const loadSupplierCcEmails = async () => {
    try {
      setLoadingCcEmails(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/settings/supplier_cc_emails', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.value) {
          setSupplierCcEmails(JSON.parse(data.value));
        }
      }
    } catch (error) {
      console.error('Error loading CC emails:', error);
    } finally {
      setLoadingCcEmails(false);
    }
  };

  // Save Supplier CC Emails
  const saveSupplierCcEmails = async (emails: string[]) => {
    try {
      const token = localStorage.getItem('token');
      await fetch('/api/settings/supplier_cc_emails', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ value: JSON.stringify(emails) }),
      });
    } catch (error) {
      console.error('Error saving CC emails:', error);
      showError('Failed to save CC emails');
    }
  };

  // Add CC Email
  const handleAddCcEmail = () => {
    if (!newCcEmail.trim()) return;
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newCcEmail.trim())) {
      showError('Please enter a valid email address');
      return;
    }
    if (supplierCcEmails.includes(newCcEmail.trim())) {
      showError('This email is already in the list');
      return;
    }
    const updatedEmails = [...supplierCcEmails, newCcEmail.trim()];
    setSupplierCcEmails(updatedEmails);
    saveSupplierCcEmails(updatedEmails);
    setNewCcEmail('');
    showSuccess('CC email added');
  };

  // Remove CC Email
  const handleRemoveCcEmail = (email: string) => {
    const updatedEmails = supplierCcEmails.filter(e => e !== email);
    setSupplierCcEmails(updatedEmails);
    saveSupplierCcEmails(updatedEmails);
    showSuccess('CC email removed');
  };

  // Load Fitter CC Emails
  const loadFitterCcEmails = async () => {
    try {
      setLoadingFitterCcEmails(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/settings/fitter_cc_emails', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.value) {
          setFitterCcEmails(JSON.parse(data.value));
        }
      }
    } catch (error) {
      console.error('Error loading fitter CC emails:', error);
    } finally {
      setLoadingFitterCcEmails(false);
    }
  };

  // Save Fitter CC Emails
  const saveFitterCcEmails = async (emails: string[]) => {
    try {
      const token = localStorage.getItem('token');
      await fetch('/api/settings/fitter_cc_emails', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ value: JSON.stringify(emails) }),
      });
    } catch (error) {
      console.error('Error saving fitter CC emails:', error);
      showError('Failed to save fitter CC emails');
    }
  };

  // Add Fitter CC Email
  const handleAddFitterCcEmail = () => {
    if (!newFitterCcEmail.trim()) return;
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newFitterCcEmail.trim())) {
      showError('Please enter a valid email address');
      return;
    }
    if (fitterCcEmails.includes(newFitterCcEmail.trim())) {
      showError('This email is already in the list');
      return;
    }
    const updatedEmails = [...fitterCcEmails, newFitterCcEmail.trim()];
    setFitterCcEmails(updatedEmails);
    saveFitterCcEmails(updatedEmails);
    setNewFitterCcEmail('');
    showSuccess('Fitter CC email added');
  };

  // Remove Fitter CC Email
  const handleRemoveFitterCcEmail = (email: string) => {
    const updatedEmails = fitterCcEmails.filter(e => e !== email);
    setFitterCcEmails(updatedEmails);
    saveFitterCcEmails(updatedEmails);
    showSuccess('Fitter CC email removed');
  };

  // Load Fitters
  const loadFitters = async () => {
    try {
      setLoadingFitters(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/fitters', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFitters(data);
      }
    } catch (error) {
      console.error('Error loading fitters:', error);
      showError('Failed to load fitters');
    } finally {
      setLoadingFitters(false);
    }
  };

  // Add Fitter
  const handleAddFitter = () => {
    setEditingFitter(null);
    setFitterForm({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      postcode: '',
      specialty: '',
      notes: '',
      is_active: true,
    });
    setFitterDialogOpen(true);
  };

  // Edit Fitter
  const handleEditFitter = (fitter: Fitter) => {
    setEditingFitter(fitter);
    setFitterForm({
      name: fitter.name || '',
      email: fitter.email || '',
      phone: fitter.phone || '',
      address: fitter.address || '',
      city: fitter.city || '',
      postcode: fitter.postcode || '',
      specialty: fitter.specialty || '',
      notes: fitter.notes || '',
      is_active: fitter.is_active,
    });
    setFitterDialogOpen(true);
  };

  // Save Fitter
  const handleSaveFitter = async () => {
    if (!fitterForm.name.trim()) {
      showError('Name is required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const url = editingFitter ? `/api/fitters/${editingFitter.id}` : '/api/fitters';
      const method = editingFitter ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(fitterForm),
      });

      if (response.ok) {
        showSuccess(editingFitter ? 'Fitter updated successfully' : 'Fitter added successfully');
        setFitterDialogOpen(false);
        loadFitters();
      } else {
        const error = await response.json();
        showError(error.message || 'Failed to save fitter');
      }
    } catch (error) {
      console.error('Error saving fitter:', error);
      showError('Failed to save fitter');
    }
  };

  // Delete Fitter
  const handleDeleteFitter = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this fitter?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/fitters/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        showSuccess('Fitter deleted successfully');
        loadFitters();
      } else {
        showError('Failed to delete fitter');
      }
    } catch (error) {
      console.error('Error deleting fitter:', error);
      showError('Failed to delete fitter');
    }
  };

  const handleSaveCompanySettings = () => {
    // In real app, this would make an API call
    showSuccess('Company settings saved successfully');
  };

  const handleSaveNotificationSettings = () => {
    // In real app, this would make an API call
    showSuccess('Notification settings saved successfully');
  };

  const handleProfileImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showError('Please select an image file');
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        showError('File size must be less than 5MB');
        return;
      }
      
      setSelectedProfileImage(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (result && typeof result === 'string') {
          setProfileImagePreview(result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOpenUserDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setUserForm({
        username: user.username,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        mobile_phone: user.mobile_phone || '',
        password: '',
        role: user.role,
        is_active: user.is_active,
      });
      if (user.profile_picture && typeof user.profile_picture === 'string' && user.profile_picture.startsWith('/')) {
        setProfileImagePreview(user.profile_picture);
      } else {
        setProfileImagePreview(null);
      }
    } else {
      setEditingUser(null);
      setUserForm({
        username: '',
        first_name: '',
        last_name: '',
        email: '',
        mobile_phone: '',
        password: '',
        role: 'user',
        is_active: true,
      });
      setProfileImagePreview(null);
    }
    setSelectedProfileImage(null);
    setUserDialogOpen(true);
  };

  const handleSaveUser = async () => {
    try {
      // Validate required fields
      if (!userForm.first_name || !userForm.last_name || !userForm.email) {
        showError('Please fill in all required fields');
        return;
      }

      if (!editingUser && !userForm.password) {
        showError('Password is required for new users');
        return;
      }

      if (!editingUser && !userForm.username) {
        showError('Username is required');
        return;
      }

      const method = editingUser ? 'PUT' : 'POST';
      const url = editingUser ? `/api/users/${editingUser.id}` : '/api/users';
      
      // Create clean user data without file references
      const userData = {
        username: userForm.username,
        email: userForm.email,
        mobile_phone: userForm.mobile_phone || null,
        first_name: userForm.first_name,
        last_name: userForm.last_name,
        role: userForm.role,
        is_active: userForm.is_active,
        ...((!editingUser && userForm.password) && { password: userForm.password })
      };
      
      console.log('Sending user data:', userData);
      console.log('Request URL:', url);
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(userData),
      });
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const savedUser = await response.json();
        
        // Upload profile picture if one was selected
        if (selectedProfileImage) {
          // Use the existing user's ID when editing, or the new user's ID when creating
          const userIdForUpload = editingUser ? editingUser.id : savedUser.user?.id;
          if (userIdForUpload) {
            await uploadProfilePicture(userIdForUpload);
          }
        }
        
        showSuccess(editingUser ? 'User updated successfully' : 'User created successfully');
        setUserDialogOpen(false);
        setSelectedProfileImage(null);
        setProfileImagePreview(null);
        await loadUsers(); // Reload users
      } else {
        const error = await response.json();
        console.error('API Error:', error);
        showError(error.message || 'Failed to save user');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      showError('Error saving user: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const uploadProfilePicture = async (userId: number) => {
    if (!selectedProfileImage) return;

    const formData = new FormData();
    formData.append('profilePicture', selectedProfileImage);

    try {
      const response = await fetch(`/api/users/${userId}/profile-picture`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Profile picture upload error:', error);
        showError('Failed to upload profile picture: ' + error.message);
      } else {
        const result = await response.json();
        console.log('Profile picture uploaded successfully:', result);
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      showError('Error uploading profile picture');
    }
  };

  const handleToggleUserStatus = async (userId: number, isActive: boolean) => {
    try {
      const response = await fetch(`/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ is_active: isActive }),
      });
      
      if (response.ok) {
        showSuccess(`User ${isActive ? 'activated' : 'deactivated'} successfully`);
        await loadUsers();
      } else {
        const error = await response.json();
        showError(error.message || 'Failed to update user status');
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      showError('Error updating user status');
    }
  };

  const handleDeleteUser = async (userId: number) => {
    const confirmed = window.confirm('Are you sure you want to delete this user?\n\nClick OK for Yes, Cancel for No.\n\nThis will permanently deactivate their account.');
    
    if (confirmed) {
      try {
        const response = await fetch(`/api/users/${userId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          showSuccess('User deleted successfully');
          await loadUsers();
        } else {
          const error = await response.json();
          showError(error.message || 'Failed to delete user');
        }
      } catch (error) {
        console.error('Error deleting user:', error);
        showError('Error deleting user');
      }
    }
  };

  const handleOpenPasswordDialog = (user: User) => {
    setChangingPasswordForUser(user);
    setPasswordForm({
      newPassword: '',
      confirmPassword: '',
    });
    setPasswordDialogOpen(true);
  };

  const handleChangePassword = async () => {
    if (!changingPasswordForUser) return;

    if (!passwordForm.newPassword) {
      showError('Please enter a new password');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      showError('Password must be at least 6 characters');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showError('Passwords do not match');
      return;
    }

    try {
      const response = await fetch(`/api/users/${changingPasswordForUser.id}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          newPassword: passwordForm.newPassword,
        }),
      });

      if (response.ok) {
        showSuccess(`Password updated successfully for ${changingPasswordForUser.first_name} ${changingPasswordForUser.last_name}`);
        setPasswordDialogOpen(false);
        setChangingPasswordForUser(null);
        setPasswordForm({
          newPassword: '',
          confirmPassword: '',
        });
      } else {
        const error = await response.json();
        showError(error.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      showError('Error changing password');
    }
  };

  // Supplier Management Functions
  const loadSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      const response = await fetch('/api/suppliers', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const supplierData = await response.json();
        setSuppliers(supplierData);
      } else {
        showError('Failed to load suppliers');
      }
    } catch (error) {
      console.error('Error loading suppliers:', error);
      showError('Error loading suppliers');
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const handleAddSupplier = () => {
    setEditingSupplier(null);
    setSupplierForm({
      company: '',
      contact_name: '',
      primary_product: '',
      email: '',
      phone: '',
      address: '',
      address_2: '',
      address_3: '',
      city: '',
      county: '',
      postcode: '',
      notes: '',
      is_active: true,
    });
    setSupplierDialogOpen(true);
  };

  const handleEditSupplier = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setSupplierForm({
      company: supplier.company || '',
      contact_name: supplier.contact_name || '',
      primary_product: supplier.primary_product || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      address: supplier.address || '',
      address_2: supplier.address_2 || '',
      address_3: supplier.address_3 || '',
      city: supplier.city || '',
      county: supplier.county || '',
      postcode: supplier.postcode || '',
      notes: supplier.notes || '',
      is_active: supplier.is_active,
    });
    setSupplierDialogOpen(true);
  };

  const handleSaveSupplier = async () => {
    if (!supplierForm.company) {
      showError('Company name is required');
      return;
    }

    try {
      const url = editingSupplier 
        ? `/api/suppliers/${editingSupplier.id}`
        : '/api/suppliers';
      
      const method = editingSupplier ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(supplierForm),
      });

      if (response.ok) {
        showSuccess(editingSupplier ? 'Supplier updated successfully' : 'Supplier created successfully');
        setSupplierDialogOpen(false);
        loadSuppliers();
      } else {
        const error = await response.json();
        showError(error.error || 'Failed to save supplier');
      }
    } catch (error) {
      console.error('Error saving supplier:', error);
      showError('Failed to save supplier');
    }
  };

  const handleDeleteSupplier = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) {
      return;
    }

    try {
      const response = await fetch(`/api/suppliers/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        showSuccess('Supplier deleted successfully');
        loadSuppliers();
      } else {
        showError('Failed to delete supplier');
      }
    } catch (error) {
      console.error('Error deleting supplier:', error);
      showError('Failed to delete supplier');
    }
  };

  // Category Management Functions
  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await fetch('/api/categories', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const categoryData = await response.json();
        setCategories(categoryData);
      } else {
        showError('Failed to load categories');
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      showError('Error loading categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleOpenCategoryDialog = () => {
    setEditingCategory(null);
    setCategoryForm({
      name: '',
      sort_order: categories.length,
      is_active: true,
    });
    setCategoryDialogOpen(true);
  };

  const handleEditCategory = (category: CategoryItem) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      sort_order: category.sort_order,
      is_active: category.is_active,
    });
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      showError('Category name is required');
      return;
    }

    try {
      const url = editingCategory
        ? `/api/categories/${editingCategory.id}`
        : '/api/categories';

      const method = editingCategory ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(categoryForm),
      });

      if (response.ok) {
        showSuccess(editingCategory ? 'Category updated successfully' : 'Category created successfully');
        setCategoryDialogOpen(false);
        loadCategories();
      } else {
        showError('Failed to save category');
      }
    } catch (error) {
      console.error('Error saving category:', error);
      showError('Error saving category');
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      const response = await fetch(`/api/categories/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        showSuccess('Category deleted successfully');
        loadCategories();
      } else {
        showError('Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      showError('Failed to delete category');
    }
  };

  // PAF Postcode Lookup Functions
  const handlePostcodeLookup = async () => {
    if (!postcodeSearch.trim()) {
      showError('Please enter a postcode');
      return;
    }

    setLoadingAddresses(true);
    try {
      const cleanPostcode = postcodeSearch.replace(/\s+/g, '').toUpperCase();
      
      // Use the new server-side endpoint
      const response = await axios.get(`/api/postcode/lookup/${cleanPostcode}`);
      
      const data = response.data;
      
      if (data.success && data.addresses && data.addresses.length > 0) {
        setAddressOptions(data.addresses);
      } else if (data.success && data.addresses.length === 0) {
        showError('No addresses found for this postcode');
        setAddressOptions([]);
      } else {
        showError(data.message || 'Error fetching addresses. Please try again.');
        setAddressOptions([]);
      }
    } catch (error: any) {
      console.error('Error fetching addresses:', error);
      const errorMessage = error.response?.data?.message || 'Error fetching addresses. Please try again.';
      showError(errorMessage);
      setAddressOptions([]);
    } finally {
      setLoadingAddresses(false);
    }
  };

  const handleAddressSelect = (address: AddressOption) => {
    // Helper function to convert to proper case
    const toProperCase = (str: string) => {
      if (!str) return '';
      return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
    };

    setSupplierForm(prev => ({
      ...prev,
      address: address.address1 || '',
      address_2: '',
      address_3: '', // Don't duplicate county here
      city: toProperCase(address.postTown || ''),
      county: address.county || '',
      postcode: address.postcode || '',
    }));
    
    setPostcodeDialogOpen(false);
    setPostcodeSearch('');
    setAddressOptions([]);
  };

  return (
    <Box>
      <Paper sx={{ width: '100%', mt: 0 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="settings tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<Business />} label="Company" />
          <Tab icon={<Person />} label="Users" />
          <Tab icon={<Notifications />} label="Notifications" />
          <Tab icon={<Security />} label="Security" />
          <Tab icon={<LocalShipping />} label="Suppliers" />
          <Tab icon={<Build />} label="Fitters" />
          <Tab icon={<Category />} label="Categories" />
        </Tabs>

        {/* Company Settings */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Company Information
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
              <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={companySettings.company_name}
                  onChange={(e) => setCompanySettings(prev => ({ ...prev, company_name: e.target.value }))}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={companySettings.phone}
                  onChange={(e) => setCompanySettings(prev => ({ ...prev, phone: e.target.value }))}
                />
              </Box>
            </Box>
            <Box>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={companySettings.address}
                onChange={(e) => setCompanySettings(prev => ({ ...prev, address: e.target.value }))}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
              <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={companySettings.email}
                  onChange={(e) => setCompanySettings(prev => ({ ...prev, email: e.target.value }))}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
                <TextField
                  fullWidth
                  label="Website"
                  value={companySettings.website}
                  onChange={(e) => setCompanySettings(prev => ({ ...prev, website: e.target.value }))}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
              <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
                <TextField
                  fullWidth
                  label="VAT Number"
                  value={companySettings.vat_number}
                  onChange={(e) => setCompanySettings(prev => ({ ...prev, vat_number: e.target.value }))}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
                <TextField
                  fullWidth
                  label="Default VAT Rate (%)"
                  type="number"
                  value={companySettings.default_vat_rate}
                  onChange={(e) => setCompanySettings(prev => ({ ...prev, default_vat_rate: e.target.value }))}
                />
              </Box>
            </Box>
            <Box>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveCompanySettings}
              >
                Save Company Settings
              </Button>
            </Box>
          </Box>
        </TabPanel>

        {/* User Management */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              User Management
            </Typography>
            {user?.role === 'admin' && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenUserDialog()}
              >
                Add User
              </Button>
            )}
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography>Loading users...</Typography>
            </Box>
          ) : (
            <List>
              {users.map((userItem) => (
                <ListItem key={userItem.id} divider>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 2 }}>
                    <Avatar
                      sx={{ 
                        width: 48, 
                        height: 48, 
                        bgcolor: 'primary.main',
                        fontSize: '1.2rem'
                      }}
                      src={userItem.profile_picture || undefined}
                    >
                      {!userItem.profile_picture && userItem.first_name && userItem.last_name
                        ? `${userItem.first_name[0]}${userItem.last_name[0]}`
                        : '?'
                      }
                    </Avatar>
                  </Box>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6">
                          {`${userItem.first_name} ${userItem.last_name}`}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            px: 1, 
                            py: 0.5, 
                            borderRadius: 1, 
                            backgroundColor: userItem.is_active ? 'success.light' : 'error.light',
                            color: userItem.is_active ? 'success.contrastText' : 'error.contrastText'
                          }}
                        >
                          {userItem.is_active ? 'Active' : 'Inactive'}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          {userItem.email} • Role: {userItem.role}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Username: {userItem.username}
                        </Typography>
                      </Box>
                    }
                  />
                  {user?.role === 'admin' && (
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={userItem.is_active}
                              onChange={(e) => handleToggleUserStatus(userItem.id, e.target.checked)}
                              size="small"
                            />
                          }
                          label=""
                          sx={{ mr: 1 }}
                        />
                        <IconButton
                          aria-label="change password"
                          onClick={() => handleOpenPasswordDialog(userItem)}
                          size="small"
                          color="primary"
                          title="Change Password"
                        >
                          <VpnKey />
                        </IconButton>
                        <IconButton
                          aria-label="edit"
                          onClick={() => handleOpenUserDialog(userItem)}
                          size="small"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          aria-label="delete"
                          onClick={() => handleDeleteUser(userItem.id)}
                          size="small"
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </TabPanel>

        {/* Notification Settings */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Notification Preferences
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.email_notifications}
                    onChange={(e) => setNotificationSettings(prev => ({ 
                      ...prev, 
                      email_notifications: e.target.checked 
                    }))}
                  />
                }
                label="Email Notifications"
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.lead_notifications}
                    onChange={(e) => setNotificationSettings(prev => ({ 
                      ...prev, 
                      lead_notifications: e.target.checked 
                    }))}
                  />
                }
                label="New Lead Notifications"
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.project_notifications}
                    onChange={(e) => setNotificationSettings(prev => ({ 
                      ...prev, 
                      project_notifications: e.target.checked 
                    }))}
                  />
                }
                label="Project Status Changes"
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.invoice_notifications}
                    onChange={(e) => setNotificationSettings(prev => ({ 
                      ...prev, 
                      invoice_notifications: e.target.checked 
                    }))}
                  />
                }
                label="Invoice Notifications"
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.overdue_reminders}
                    onChange={(e) => setNotificationSettings(prev => ({ 
                      ...prev, 
                      overdue_reminders: e.target.checked 
                    }))}
                  />
                }
                label="Overdue Reminders"
              />
            </Box>
            <Box>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveNotificationSettings}
              >
                Save Notification Settings
              </Button>
            </Box>
          </Box>
        </TabPanel>

        {/* Security Settings */}
        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom>
            Security Settings
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Change Password
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <TextField
                        fullWidth
                        label="Current Password"
                        type="password"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                      <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
                        <TextField
                          fullWidth
                          label="New Password"
                          type="password"
                        />
                      </Box>
                      <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
                        <TextField
                          fullWidth
                          label="Confirm New Password"
                          type="password"
                        />
                      </Box>
                    </Box>
                    <Box>
                      <Button variant="contained">
                        Update Password
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
            <Box>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Security Options
                  </Typography>
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Require strong passwords"
                  />
                  <br />
                  <FormControlLabel
                    control={<Switch />}
                    label="Enable two-factor authentication"
                  />
                  <br />
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Session timeout after 24 hours"
                  />
                </CardContent>
              </Card>
            </Box>
          </Box>
        </TabPanel>

        {/* Suppliers Management */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Supplier Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddSupplier}
            >
              Add Supplier
            </Button>
          </Box>

          {/* CC Emails Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Supplier Email CC List
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                These email addresses will be CC'd on all supplier emails sent from the system.
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Enter email address"
                  value={newCcEmail}
                  onChange={(e) => setNewCcEmail(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddCcEmail();
                    }
                  }}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddCcEmail}
                  disabled={!newCcEmail.trim()}
                >
                  Add
                </Button>
              </Box>

              {loadingCcEmails ? (
                <CircularProgress size={20} />
              ) : supplierCcEmails.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No CC emails configured.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {supplierCcEmails.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={() => handleRemoveCcEmail(email)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          {loadingSuppliers ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : suppliers.length === 0 ? (
            <Card>
              <CardContent>
                <Typography color="textSecondary" align="center">
                  No suppliers found. Click "Add Supplier" to create one.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <List>
              {suppliers.map((supplier) => (
                <ListItem
                  key={supplier.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {supplier.company}
                        </Typography>
                        {supplier.primary_product && (
                          <Typography variant="body2" color="textSecondary">
                            ({supplier.primary_product})
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        {supplier.contact_name && (
                          <Typography variant="body2">
                            Contact: {supplier.contact_name}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 3, mt: 0.5 }}>
                          {supplier.phone && (
                            <Typography variant="body2" color="textSecondary">
                              Phone: {supplier.phone}
                            </Typography>
                          )}
                          {supplier.email && (
                            <Typography variant="body2" color="textSecondary">
                              Email: {supplier.email}
                            </Typography>
                          )}
                        </Box>
                        {supplier.address && (
                          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                            {supplier.address}, {supplier.city} {supplier.postcode}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      onClick={() => handleEditSupplier(supplier)}
                      sx={{ mr: 1 }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => supplier.id && handleDeleteSupplier(supplier.id)}
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </TabPanel>

        {/* Fitters Tab */}
        <TabPanel value={tabValue} index={5}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Fitter Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddFitter}
            >
              Add Fitter
            </Button>
          </Box>

          {/* Fitter CC Emails Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Fitter Email CC List
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                These email addresses will be CC'd when sending documents to fitters.
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Enter CC email address"
                  value={newFitterCcEmail}
                  onChange={(e) => setNewFitterCcEmail(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddFitterCcEmail();
                    }
                  }}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddFitterCcEmail}
                  disabled={!newFitterCcEmail.trim()}
                >
                  Add
                </Button>
              </Box>

              {loadingFitterCcEmails ? (
                <CircularProgress size={20} />
              ) : fitterCcEmails.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No CC emails configured.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {fitterCcEmails.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={() => handleRemoveFitterCcEmail(email)}
                      color="secondary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Manage fitters who work on jobs. Their email addresses will be used when sending documents using the "Send to Fitters" button.
          </Typography>

          {loadingFitters ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : fitters.length === 0 ? (
            <Card>
              <CardContent>
                <Typography color="textSecondary" align="center">
                  No fitters found. Click "Add Fitter" to create one.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <List>
              {fitters.map((fitter) => (
                <ListItem
                  key={fitter.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {fitter.name}
                        </Typography>
                        {fitter.specialty && (
                          <Typography variant="body2" color="textSecondary">
                            ({fitter.specialty})
                          </Typography>
                        )}
                        {!fitter.is_active && (
                          <Chip label="Inactive" size="small" color="default" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        {fitter.email && (
                          <Typography variant="body2" component="span" sx={{ mr: 2 }}>
                            Email: {fitter.email}
                          </Typography>
                        )}
                        {fitter.phone && (
                          <Typography variant="body2" component="span" sx={{ mr: 2 }}>
                            Phone: {fitter.phone}
                          </Typography>
                        )}
                        {fitter.postcode && (
                          <Typography variant="body2" component="span">
                            Postcode: {fitter.postcode}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => handleEditFitter(fitter)} sx={{ mr: 1 }}>
                      <Edit />
                    </IconButton>
                    <IconButton edge="end" onClick={() => handleDeleteFitter(fitter.id!)}>
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </TabPanel>

        {/* Categories Tab */}
        <TabPanel value={tabValue} index={6}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Order Item Categories
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenCategoryDialog}
            >
              Add Category
            </Button>
          </Box>

          {loadingCategories ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : categories.length === 0 ? (
            <Card>
              <CardContent>
                <Typography color="textSecondary" align="center">
                  No categories found. Click "Add Category" to create one.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <List>
              {categories.map((category) => (
                <ListItem
                  key={category.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" fontWeight="bold">
                        {category.name}
                      </Typography>
                    }
                    secondary={`Sort Order: ${category.sort_order}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      onClick={() => handleEditCategory(category)}
                      sx={{ mr: 1 }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => category.id && handleDeleteCategory(category.id)}
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </TabPanel>
      </Paper>

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 50%' } }}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={userForm.first_name}
                  onChange={(e) => setUserForm(prev => ({ ...prev, first_name: e.target.value }))}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 50%' } }}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={userForm.last_name}
                  onChange={(e) => setUserForm(prev => ({ ...prev, last_name: e.target.value }))}
                />
              </Box>
            </Box>
            <Box>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
              />
            </Box>
            <Box>
              <TextField
                fullWidth
                label="Mobile Phone"
                type="tel"
                value={userForm.mobile_phone}
                onChange={(e) => setUserForm(prev => ({ ...prev, mobile_phone: e.target.value }))}
                placeholder="e.g., 07123456789"
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 50%' } }}>
                <TextField
                  fullWidth
                  label="Username"
                  value={userForm.username}
                  onChange={(e) => setUserForm(prev => ({ ...prev, username: e.target.value }))}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 50%' } }}>
                <TextField
                  fullWidth
                  select
                  label="Role"
                  value={userForm.role}
                  onChange={(e) => setUserForm(prev => ({ ...prev, role: e.target.value }))}
                  SelectProps={{ native: true }}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </TextField>
              </Box>
            </Box>
            {!editingUser && (
              <Box>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                />
              </Box>
            )}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Profile Picture
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    bgcolor: 'primary.main',
                    fontSize: '1.5rem'
                  }}
                  src={profileImagePreview || undefined}
                >
                  {!profileImagePreview && userForm.first_name && userForm.last_name
                    ? `${userForm.first_name[0]}${userForm.last_name[0]}`
                    : ''
                  }
                </Avatar>
                <Box>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="profile-picture-upload"
                    type="file"
                    onChange={handleProfileImageChange}
                  />
                  <label htmlFor="profile-picture-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<PhotoCamera />}
                      size="small"
                    >
                      Upload Photo
                    </Button>
                  </label>
                  {selectedProfileImage && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      {selectedProfileImage.name}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={userForm.is_active}
                    onChange={(e) => setUserForm(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label="Active User"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained">
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Change Password for {changingPasswordForUser?.first_name} {changingPasswordForUser?.last_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="textSecondary">
              As an administrator, you can set a new password for this user without requiring their current password.
            </Typography>
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
              helperText="Password must be at least 6 characters"
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
              error={passwordForm.confirmPassword !== '' && passwordForm.newPassword !== passwordForm.confirmPassword}
              helperText={
                passwordForm.confirmPassword !== '' && passwordForm.newPassword !== passwordForm.confirmPassword
                  ? "Passwords do not match"
                  : ""
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleChangePassword} 
            variant="contained"
            disabled={!passwordForm.newPassword || passwordForm.newPassword !== passwordForm.confirmPassword}
          >
            Change Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Supplier Dialog */}
      <Dialog open={supplierDialogOpen} onClose={() => setSupplierDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Company Name *"
                value={supplierForm.company}
                onChange={(e) => setSupplierForm(prev => ({ ...prev, company: e.target.value }))}
                error={!supplierForm.company}
                helperText={!supplierForm.company ? 'Company name is required' : ''}
              />
              <TextField
                fullWidth
                label="Primary Product"
                value={supplierForm.primary_product}
                onChange={(e) => setSupplierForm(prev => ({ ...prev, primary_product: e.target.value }))}
                placeholder="What they mainly supply"
              />
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Contact Name"
                value={supplierForm.contact_name}
                onChange={(e) => setSupplierForm(prev => ({ ...prev, contact_name: e.target.value }))}
              />
              <TextField
                fullWidth
                label="Phone"
                value={supplierForm.phone}
                onChange={(e) => setSupplierForm(prev => ({ ...prev, phone: e.target.value }))}
              />
            </Box>
            
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={supplierForm.email}
              onChange={(e) => setSupplierForm(prev => ({ ...prev, email: e.target.value }))}
            />
            
            <Divider />
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                fullWidth
                label="Postcode"
                value={supplierForm.postcode}
                onChange={(e) => setSupplierForm(prev => ({ ...prev, postcode: e.target.value }))}
              />
              <Button
                variant="outlined"
                startIcon={<Search />}
                onClick={() => {
                  if (supplierForm.postcode) {
                    setPostcodeSearch(supplierForm.postcode);
                    setAddressOptions([]);
                    setPostcodeDialogOpen(true);
                  } else {
                    showError('Please enter a postcode first');
                  }
                }}
                sx={{ minWidth: 150 }}
              >
                Lookup Address
              </Button>
            </Box>
            
            <TextField
              fullWidth
              label="Address Line 1"
              value={supplierForm.address}
              onChange={(e) => setSupplierForm(prev => ({ ...prev, address: e.target.value }))}
            />
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={supplierForm.address_2}
                onChange={(e) => setSupplierForm(prev => ({ ...prev, address_2: e.target.value }))}
              />
              <TextField
                fullWidth
                label="Address Line 3"
                value={supplierForm.address_3}
                onChange={(e) => setSupplierForm(prev => ({ ...prev, address_3: e.target.value }))}
              />
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                fullWidth
                label="City"
                value={supplierForm.city}
                onChange={(e) => setSupplierForm(prev => ({ ...prev, city: e.target.value }))}
              />
              <TextField
                fullWidth
                label="County"
                value={supplierForm.county}
                onChange={(e) => setSupplierForm(prev => ({ ...prev, county: e.target.value }))}
              />
            </Box>
            
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={supplierForm.notes}
              onChange={(e) => setSupplierForm(prev => ({ ...prev, notes: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSupplierDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveSupplier} 
            variant="contained"
            disabled={!supplierForm.company}
          >
            {editingSupplier ? 'Update' : 'Create'} Supplier
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fitter Dialog */}
      <Dialog open={fitterDialogOpen} onClose={() => setFitterDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingFitter ? 'Edit Fitter' : 'Add New Fitter'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Name *"
                value={fitterForm.name}
                onChange={(e) => setFitterForm(prev => ({ ...prev, name: e.target.value }))}
                error={!fitterForm.name}
                helperText={!fitterForm.name ? 'Name is required' : ''}
              />
              <TextField
                fullWidth
                label="Specialty"
                value={fitterForm.specialty}
                onChange={(e) => setFitterForm(prev => ({ ...prev, specialty: e.target.value }))}
                placeholder="e.g. Kitchens, Bathrooms"
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={fitterForm.email}
                onChange={(e) => setFitterForm(prev => ({ ...prev, email: e.target.value }))}
              />
              <TextField
                fullWidth
                label="Phone"
                value={fitterForm.phone}
                onChange={(e) => setFitterForm(prev => ({ ...prev, phone: e.target.value }))}
              />
            </Box>

            <Divider />

            <TextField
              fullWidth
              label="Address"
              value={fitterForm.address}
              onChange={(e) => setFitterForm(prev => ({ ...prev, address: e.target.value }))}
            />

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                fullWidth
                label="City"
                value={fitterForm.city}
                onChange={(e) => setFitterForm(prev => ({ ...prev, city: e.target.value }))}
              />
              <TextField
                fullWidth
                label="Postcode"
                value={fitterForm.postcode}
                onChange={(e) => setFitterForm(prev => ({ ...prev, postcode: e.target.value }))}
              />
            </Box>

            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={fitterForm.notes}
              onChange={(e) => setFitterForm(prev => ({ ...prev, notes: e.target.value }))}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={fitterForm.is_active}
                  onChange={(e) => setFitterForm(prev => ({ ...prev, is_active: e.target.checked }))}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFitterDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveFitter}
            variant="contained"
            disabled={!fitterForm.name}
          >
            {editingFitter ? 'Update' : 'Create'} Fitter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Postcode Lookup Dialog */}
      <Dialog open={postcodeDialogOpen} onClose={() => setPostcodeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Postcode Lookup</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Enter Postcode"
              value={postcodeSearch}
              onChange={(e) => setPostcodeSearch(e.target.value)}
              placeholder="e.g. BL1 1AA"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handlePostcodeLookup();
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handlePostcodeLookup}
              disabled={loadingAddresses || !postcodeSearch.trim()}
            >
              {loadingAddresses ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Search Addresses'
              )}
            </Button>
            
            {addressOptions.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Select an address:
                </Typography>
                <List sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {addressOptions.map((address, index) => (
                    <ListItemButton
                      key={index}
                      onClick={() => handleAddressSelect(address)}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemText
                        primary={address.summaryline || address.address1}
                        secondary={`${address.postTown || ''} ${address.postcode || ''}`}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPostcodeDialogOpen(false);
            setAddressOptions([]);
            setPostcodeSearch('');
          }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Edit Category' : 'Add New Category'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Category Name"
              value={categoryForm.name}
              onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
              autoFocus
            />
            <TextField
              fullWidth
              label="Sort Order"
              type="number"
              value={categoryForm.sort_order}
              onChange={(e) => setCategoryForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
              helperText="Lower numbers appear first"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveCategory}>
            {editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}